/**
 * API client for admin CRUD of the Learn-screen quiz curriculum
 * (units → lessons → questions).
 *
 * Backend: routes/admin_quiz_content.py.
 */

import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Types
// ============================================================

// ============================================================
// feat-284: the additive translation overlay on the admin tree
// ============================================================

/**
 * Per-field review status of a translation. Present in the payload, and typed
 * here so a consumer that DOES care can read it — but `/quiz-review` renders
 * neither value. `fresh` vs `approved` is the `/quiz-translations` queue's
 * business; surfacing it on a reading surface would turn it into a second
 * queue (feat-284 spec, "Nothing else about translation status is surfaced").
 */
export type TranslationFieldStatus = 'fresh' | 'approved';

/** One translated field: the text, its review status, and whether its English
 *  source has changed since the translation was made. `stale` is DERIVED
 *  server-side from `source_version` vs `sha256(canonical)` and is never
 *  stored (ADR 0001 D2) — do not try to re-derive it in the client. */
export interface TranslatedField {
  /** UNTRUSTED (`content_translation.translated_text`, ADR 0004 D4 / C5b).
   *  Render as a JSX text node. Never `dangerouslySetInnerHTML`. */
  text: string;
  status: TranslationFieldStatus;
  stale: boolean;
}

/**
 * feat-284 — the ADDITIVE overlay `GET /api/tenant/{id}/learn-content?lang=xx`
 * hangs off each entity. Canonical English fields are UNCHANGED beside it; the
 * overlay never replaces them. (This is the deliberate point of departure from
 * the mobile path, `mobile_api.py:2425`, which overwrites `unit["title"]` — a
 * shape that cannot express Both mode because the English is gone.)
 *
 * Keyed by language code, then by field name. **An absent field key means
 * UNTRANSLATED** — not `null`, not `''`. That absence is the only signal the
 * "English text, marked" rendering has, so it must survive every `??`/`||`
 * between here and the JSX. An entity with no `translations` key at all is
 * entirely untranslated.
 *
 * `Partial<Record<...>>` rather than `Record<...>` is load-bearing for the same
 * reason: it forces call sites to handle `undefined` per field.
 */
export type TranslationOverlay<TField extends string> = Partial<
  Record<string, Partial<Record<TField, TranslatedField>>>
>;

/** Field names carrying a translation, per entity type. These mirror
 *  `TRANSLATABLE_FIELDS` (`services/content_translation.py:52`) exactly; feat-284
 *  makes no new field translatable. */
export type UnitTranslatableField = 'title' | 'subtitle';
export type DrillTranslatableField = 'title';
export type QuestionTranslatableField =
  | 'question_text'
  | 'option_a'
  | 'option_b'
  | 'option_c'
  | 'option_d'
  | 'explanation'
  | 'correct_explanation';
export type ReadingTranslatableField = 'title' | 'body';
export type RehearsalTranslatableField =
  | 'name'
  | 'scenario_text'
  | 'location_label'
  | 'correct_example_response';
/** NOTE: `example_phrases` is deliberately absent — it is a SCORING field fed to
 *  the grader, not learner-facing copy, and is never translated. */
export type RehearsalCriterionTranslatableField = 'label' | 'description';

export type CorrectOption = 'a' | 'b' | 'c' | 'd';

/** feat-033: question content type. `multiple_choice` is the classic A/B/C/D
 *  drill; `fill_blank` reuses the same `option_a..d` + `correct_option` shape as
 *  the MC-fallback distractors, with `question_text` carrying a `___` blank
 *  marker and the correct option's text being the answer that fills the blank
 *  (ADR 0001 §D1). */
export type QuestionType = 'multiple_choice' | 'fill_blank';

/** feat-325: the stored override values. `null` (never a string) means Auto. */
export type BlankGradingMode = 'strict' | 'lenient';

export interface QuizQuestion {
  id: string;
  /** feat-198 (ADR 0001 §D-P.2): was `lesson_id`. Points UP at the owning Drill,
   *  so it takes the child-pointer name `drill_id`, mirroring the column. */
  drill_id?: string;
  /** feat-033: discriminates MC vs fill-in-the-blank authoring. Defaults to
   *  `multiple_choice` for legacy rows (backend column default). */
  question_type: QuestionType;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: CorrectOption;
  explanation: string;
  correct_explanation: string;
  /** feat-033: per-question authored XP. NULL/undefined ⇒ the backend resolves
   *  a per-type default at read time (ADR 0001 §OQ-C). */
  xp_value: number | null;
  /** feat-075 (ADR 0011): newline-delimited accepted answer variants for
   *  fill-blank grading. Each line is an additional answer the grader accepts
   *  for a typed/spoken response. Fill-blank ONLY; never shown to learners. The
   *  canonical answer (option_a..d at correct_option) is implicitly accepted, so
   *  it does NOT need to be repeated here. NULL/undefined ⇒ no variants (grades
   *  exactly as today). The backend normalizes/dedupes/drops-canonical on write
   *  and is the authoritative validator (≤10 variants, ≤64 chars each). */
  blank_accepted_variants?: string | null;
  /** feat-325 (CONTRACT §2): per-question AI grading override for fill-blank.
   *  null/undefined ⇒ Auto (strict when the question text contains quotes or the
   *  answer is the company name, otherwise lenient). Fill-blank ONLY; the backend
   *  stores NULL for multiple_choice. Not approval-gated (CONTRACT r2). */
  blank_grading_mode?: BlankGradingMode | null;
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;
  sort_order: number;
  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this question is entirely untranslated. */
  translations?: TranslationOverlay<QuestionTranslatableField>;

}

/** A rehearsal step that lives inline in a lesson. Each row owns its own
 *  scenario, criteria, XP value, and max recording length — there is no
 *  shared template; if a rehearsal needs to live in two lessons, copy it. */
export type RehearsalCriterionKind = 'good' | 'bad';

export interface RehearsalCriterion {
  id?: string;
  kind: RehearsalCriterionKind;
  label: string;
  description: string;
  example_phrases?: string | null;
  position: number;
  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this criterion is entirely untranslated. */
  translations?: TranslationOverlay<RehearsalCriterionTranslatableField>;

}

export interface RehearsalLessonItem {
  id: string;
  /** feat-198: was `lesson_id`. `rehearsal_template` is NOT renamed — only this
   *  column is, which is why no table-name census can see this site (§D-N). */
  drill_id: string | null;
  role: string;
  name: string;
  scenario_text: string;
  scenario_image_s3_key: string | null;
  prompt_template_version_id: string | null;
  prompt_template_version_number: number | null;
  xp_value: number;
  /**
   * feat-033 (ADR 0001): the customer/setting label shown on the rehearsal
   * screen banner (e.g. "Drive-thru, lunch rush"). Translatable; English is
   * canonical. NULL on legacy rows.
   */
  location_label: string | null;
  /**
   * feat-033 (ADR 0001): a model answer surfaced in the "Try saying" card when
   * the learner's spoken attempt fails AI grading. Translatable; English is
   * canonical. NULL on legacy rows.
   */
  correct_example_response: string | null;
  /**
   * XP awarded on every passing attempt *after* the first. Must satisfy
   * `0 <= redo_xp <= xp_value`; the backend enforces the upper bound with
   * HTTP 400 and the editor mirrors the check client-side. Defaults to 0
   * (existing behaviour: redo passes award nothing).
   */
  redo_xp: number;
  /**
   * XP awarded on a failed attempt (`passed=False`). Must satisfy
   * `0 <= fail_xp <= xp_value`; backend enforces the upper bound with
   * HTTP 400 and the editor mirrors the check client-side. Defaults to 0
   * (failed attempts award nothing — existing behaviour). The soft-failure
   * path (`passed IS NULL`) still awards 0 regardless of this value.
   */
  fail_xp: number;
  max_recording_seconds: number;
  sort_order: number;
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;
  /** feat-252: server-owned last-modified timestamp, ISO-8601 UTC (`...Z`). Only
   *  the three tables that carry an `updated` column emit this. Optional and
   *  read-only, same as {@link created}. */
  updated?: string;
  criteria: RehearsalCriterion[];

  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this rehearsal is entirely untranslated. */
  translations?: TranslationOverlay<RehearsalTranslatableField>;
}

export interface QuizLesson {
  id: string;
  /** feat-198 (ADR 0001 §D-P.1): was `unit_id`. This is a Drill's pointer UP to
   *  its owning Lesson, and a pointer up is ALWAYS `parent_lesson_id`, never
   *  `lesson_id` — `lesson_id` is reserved for a level-1 entity's OWN id. */
  parent_lesson_id?: string;
  title: string;
  emoji: string;
  /** feat-033 (ADR 0001 §OQ-D): key from the frozen {@link LESSON_ICON_CATALOG}
   *  picked by the admin; Flutter maps it to the vertical-path icon. NULL ⇒ the
   *  app falls back to the free-form `emoji` (preserving existing data). */
  icon_key: string | null;
  xp_reward: number;
  /** NULL/undefined = show every question in the bank per session. */
  question_count: number | null;
  sort_order: number;
  active: boolean;
  /** feat-198 (ADR 0001 §D-P.2): was `questions`. */
  question_items: QuizQuestion[];
  rehearsal_items: RehearsalLessonItem[];
  /** feat-165: explanatory video items in this drill. The admin tree
   *  (`get_quiz_content`) serializes these alongside `question_items`/`rehearsal_items`;
   *  older backends omit the key, so treat undefined as `[]`. */
  video_items?: VideoItemSummary[];
  /** feat-166: reading items (markdown body + optional ordered images) in this
   *  drill. Serialized alongside `video_items`; older backends omit the key, so
   *  treat undefined as `[]`. */
  reading_items?: ReadingItemSummary[];
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;

  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this drill is entirely untranslated. */
  translations?: TranslationOverlay<DrillTranslatableField>;
}

// ============================================================
// feat-165: explanatory video item
// ============================================================

/** Backend-authoritative lifecycle of the async captions job (never classified in
 *  the FE): 'pending' = transcode+Deepgram+Claude running; 'ready' = cues exist
 *  and can be reviewed/published; 'failed' = the job errored (offer Retry). */
export type VideoCaptionsStatus = 'pending' | 'ready' | 'failed';

/** The compact row the editor tree renders for a video item (from
 *  `get_quiz_content` → `lesson.video_items`). The full captions payload
 *  (cues + video_url) is fetched lazily via {@link getVideoItemCaptions}. */
export interface VideoItemSummary {
  id: string;
  /** feat-198: was `lesson_id`. `video_item` is NOT renamed — only this column. */
  drill_id: string | null;
  title: string;
  sort_order: number;
  captions_status: VideoCaptionsStatus;
  /** ffprobe-authoritative duration; NULL until the job probes it. */
  duration_seconds: number | null;
  published: boolean;
  /** Present once the transcode job has written the playable MP4. */
  video_s3_key?: string | null;
  caption_en_s3_key?: string | null;
  caption_es_s3_key?: string | null;
  /** Public URL of the transcoded MP4, or null until captions_status='ready'
   *  (backend builds it; the review pane also gets it from getVideoItemCaptions). */
  video_url?: string | null;
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;
  /** feat-252: server-owned last-modified timestamp, ISO-8601 UTC (`...Z`). Only
   *  the three tables that carry an `updated` column emit this. Optional and
   *  read-only, same as {@link created}. */
  updated?: string;
}

/** One timed caption cue. Timings are READ-ONLY in the editor (locked to the
 *  audio); `en` is the source of truth and `es` is derived from it unless
 *  `es_manually_edited` (D-165.2). `es` is null when translation failed. */
export interface VideoCaptionCue {
  index: number;
  start_ms: number;
  end_ms: number;
  en: string;
  es: string | null;
  es_manually_edited: boolean;
}

/** Response of {@link getVideoItemCaptions} — the review payload the editor polls
 *  across the async gap and renders the EN|ES grid from. */
export interface VideoCaptionsPayload {
  id: string;
  title: string;
  captions_status: VideoCaptionsStatus;
  published: boolean;
  /** Public MP4 URL; null until the transcode job finishes. */
  video_url: string | null;
  duration_seconds: number | null;
  cues: VideoCaptionCue[];
}

/** Content types the video uploader accepts. S3 hard-enforces this exact set as a
 *  presigned-POST condition (backend `VIDEO_CONTENT_TYPES`); the client pre-checks
 *  only for a friendly message before the presign round-trip. */
export const VIDEO_ALLOWED_TYPES: ReadonlyArray<string> = [
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov
];

/** Max raw upload size — 200 MB (backend `VIDEO_MAX_BYTES`, D4). S3 is the
 *  authority (`content-length-range`); the client pre-check avoids a wasted PUT. */
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;

/** Max intro-video duration — 180 s (backend `VIDEO_MAX_DURATION_SECONDS`, D4).
 *  ffprobe in the captions job is the authoritative gate; the client pre-check +
 *  create-endpoint check are best-effort UX. */
export const VIDEO_MAX_DURATION_SECONDS = 180;

/** The presigned-POST policy the backend mints for a raw video upload. `upload.fields`
 *  must be appended to the FormData FIRST and the file LAST (S3 requires the file
 *  last). S3 returns HTTP 204 on success. `max_duration_seconds` is advisory. */
export interface VideoUploadPolicy {
  s3_key: string;
  upload: { url: string; fields: Record<string, string> };
  expires_in: number;
  max_bytes: number;
  max_duration_seconds: number;
}

/**
 * Step 1 of a video upload: ask the backend (admin-gated, tenant-scoped) for a
 * presigned POST policy targeting the PRIVATE bucket. The server builds the key
 * from validated parts (`filename` is advisory, never used in the key) and hard-
 * enforces the content-type allowlist + 200 MB cap as S3 POST conditions.
 */
export async function requestVideoUploadUrl(
  lessonId: string,
  body: { tenant_id: string; content_type: string; filename?: string },
): Promise<VideoUploadPolicy> {
  return fetchApi(
    `/api/admin/learn-content/drills/${encodeURIComponent(lessonId)}/video-upload-url`,
    { method: 'POST', headers: getAuthHeaders(), body },
  );
}

/**
 * Why a failed video upload failed, as far as the browser can actually tell.
 * feat-246: the old code collapsed every XHR-level failure into "network error",
 * which sent a production reporter to check their wifi when the real fault was a
 * missing S3 CORS configuration. Each member below is a genuinely different thing
 * to do next.
 *
 * - `blocked`     — `xhr.status === 0`: the request never left the browser. CORS
 *                   preflight, an extension, or a dead connection; the browser
 *                   does not tell us which, so neither do we.
 * - `rejected`    — S3 answered with a real non-2xx HTTP status. Carries `status`.
 * - `cancelled`   — the caller's own `AbortSignal` fired. Not a failure.
 * - `interrupted` — any other abort, or a timeout: bytes were moving and stopped.
 */
export type VideoUploadErrorKind = 'blocked' | 'rejected' | 'cancelled' | 'interrupted';

/** `Error` subtype rejected by {@link uploadVideoToS3}. It stays an `Error` and its
 *  `message` stays a complete human sentence, because callers render it directly in
 *  the "Couldn't save the video" banner; `kind`/`status` are additive. */
export interface VideoUploadError extends Error {
  kind: VideoUploadErrorKind;
  /** Present only on `kind === 'rejected'`. */
  status?: number;
}

function videoUploadError(
  kind: VideoUploadErrorKind,
  message: string,
  status?: number,
): VideoUploadError {
  const err = new Error(message) as VideoUploadError;
  err.kind = kind;
  if (status !== undefined) err.status = status;
  return err;
}

/**
 * Upload the raw video bytes straight to S3 via a presigned POST (bytes never
 * touch our backend). Uses XMLHttpRequest so the caller gets upload progress
 * (0–1) for the mockup §D2 progress bar — `fetch` exposes no upload progress.
 * Policy fields go FIRST, the file LAST (S3 requirement). S3 replies 204.
 * Returns the resolver on success; rejects with a {@link VideoUploadError} whose
 * `message` names only what is actually known (feat-246 AC17).
 */
export function uploadVideoToS3(
  file: File,
  policy: VideoUploadPolicy,
  opts?: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<{ s3_key: string }> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(policy.upload.fields)) form.append(k, v);
    form.append('file', file); // file LAST

    // Distinguishes a cancel the caller asked for from an abort that just happened.
    let cancelledByCaller = false;

    // `xhr.status === 0` on error: the request never left the browser, so there is no
    // status and no response to reason from. Name all three possible causes and claim
    // none — `CORS/preflight` is here because it is the term that finds the bucket
    // config, and it is the one the old "network error" hid.
    const blocked = () =>
      videoUploadError(
        'blocked',
        'The upload was blocked before it left your browser, so nothing reached storage. ' +
          'That can be your network, a browser extension, or a site configuration problem ' +
          "(CORS/preflight) \u2014 the browser doesn't say which. Try again; if it keeps failing, " +
          'send this message to your Bespoke contact.',
      );

    // A real HTTP status from S3. No guess at which rejection it is: 403-expired-policy
    // and 400-too-large are indistinguishable from the browser, and `xhr.statusText` is
    // empty or generic on a cross-origin response, so it is deliberately not included.
    const rejectedByStorage = (status: number) =>
      videoUploadError(
        'rejected',
        `Storage rejected the upload (HTTP ${status}). The video reached storage but wasn't ` +
          'accepted, so nothing was saved. Try again; if the same code comes back, send this ' +
          'message to your Bespoke contact.',
        status,
      );

    // Bytes were moving and stopped. A network cause is honest here in a way it is not
    // for `blocked`. The staged clip survives in component state, hence "still here".
    const interrupted = () =>
      videoUploadError(
        'interrupted',
        'The upload stopped partway through, so nothing was saved. Your recording is still ' +
          'here \u2014 check your connection and try again.',
      );

    const cancelled = () => videoUploadError('cancelled', 'Upload cancelled');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', policy.upload.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts?.onProgress) opts.onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve({ s3_key: policy.s3_key });
      else reject(rejectedByStorage(xhr.status));
    };
    // `onerror` is status 0 in practice; the non-zero arm is a belt-and-braces guard so
    // no path can report "blocked before it left your browser" about a real HTTP reply.
    xhr.onerror = () => reject(xhr.status === 0 ? blocked() : rejectedByStorage(xhr.status));
    xhr.onabort = () => reject(cancelledByCaller ? cancelled() : interrupted());
    xhr.ontimeout = () => reject(interrupted());
    if (opts?.signal) {
      if (opts.signal.aborted) {
        cancelledByCaller = true;
        xhr.abort();
        // `abort()` before `send()` fires no abort event, so settle explicitly — otherwise
        // an already-aborted signal leaves the caller awaiting a promise forever and the
        // user is told nothing at all, which is the same defect AC17 exists to fix.
        reject(cancelled());
        return;
      }
      opts.signal.addEventListener('abort', () => {
        cancelledByCaller = true;
        xhr.abort();
      });
    }
    xhr.send(form);
  });
}

/**
 * Create a video item from an already-uploaded raw `source_s3_key`. The backend
 * inserts it `captions_status='pending', published=FALSE`, enqueues the async
 * captions job, and audits the create (feat-164). Returns the new id + status;
 * poll {@link getVideoItemCaptions} for the job result.
 */
export async function createVideoItem(
  lessonId: string,
  data: {
    tenant_id: string;
    title: string;
    sort_order?: number;
    source_s3_key: string;
    duration_seconds?: number | null;
  },
): Promise<{ id: string; captions_status: VideoCaptionsStatus }> {
  return fetchApi(
    `/api/admin/learn-content/drills/${encodeURIComponent(lessonId)}/video-items`,
    { method: 'POST', headers: getAuthHeaders(), body: data },
  );
}

/** Edit a video item's title / sort_order (audited update). Captions + timings
 *  are edited via the caption endpoints, not here. */
export async function updateVideoItem(
  videoItemId: string,
  data: Partial<{ title: string; sort_order: number }>,
): Promise<{ status: string }> {
  return fetchApi(`/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

/** Attach a NEW raw upload to an existing item: resets it to pending/unpublished,
 *  DISCARDS the old cues (they described the old audio — prior EN/ES edits are
 *  intentionally dropped, D6), and re-enqueues the captions job. */
export async function replaceVideoItemSource(
  videoItemId: string,
  data: { source_s3_key: string },
): Promise<{ status: string; captions_status: VideoCaptionsStatus }> {
  return fetchApi(
    `/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/replace`,
    { method: 'POST', headers: getAuthHeaders(), body: data },
  );
}

/** Soft-delete (archived=TRUE) so the audit trail keeps its target. Audited delete. */
export async function deleteVideoItem(videoItemId: string): Promise<void> {
  await fetchApi(`/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

/** Review payload: captions_status (poll this across the async gap), the playable
 *  video URL, duration, and the editable cue list. */
export async function getVideoItemCaptions(videoItemId: string): Promise<VideoCaptionsPayload> {
  return fetchApi(`/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/captions`, {
    headers: getAuthHeaders(),
  });
}

/**
 * Batch EN edit → RE-SYNC ES (D-165.2 / AT3). Sends every currently-edited cue's
 * `{index, en}`; the backend re-translates ES for ONLY the cues whose `en` text
 * actually changed and resets their `es_manually_edited` to FALSE (unchanged cues
 * are untouched). Returns the full, refreshed cue list.
 */
export async function updateVideoCaptionsEn(
  videoItemId: string,
  cues: Array<{ index: number; en: string }>,
): Promise<{ cues: VideoCaptionCue[] }> {
  return fetchApi(
    `/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/captions/en`,
    { method: 'PUT', headers: getAuthHeaders(), body: { cues } },
  );
}

/** Manager ES fine-tune (override). Sets `es_manually_edited=TRUE`; the editor
 *  then shows the "a future English edit will overwrite this" warning, and the
 *  next EN edit of that cue does exactly that (D-165.2). Returns the updated cue. */
export async function updateVideoCaptionEs(
  videoItemId: string,
  data: { index: number; es: string },
): Promise<{ cue: VideoCaptionCue }> {
  return fetchApi(
    `/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/captions/es`,
    { method: 'PUT', headers: getAuthHeaders(), body: data },
  );
}

/** Discard a manager ES override: re-translate `es` from the current `en` and
 *  clear `es_manually_edited` (back to auto). Returns the updated cue. */
export async function resetVideoCaptionEs(
  videoItemId: string,
  index: number,
): Promise<{ cue: VideoCaptionCue }> {
  return fetchApi(
    `/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/captions/${index}/reset-es`,
    { method: 'POST', headers: getAuthHeaders() },
  );
}

/** Re-enqueue the captions job (for captions_status='failed' — e.g. after a
 *  deploy-orphaned job). Idempotent: resets the row to 'pending' and re-runs. */
export async function retryVideoCaptions(
  videoItemId: string,
): Promise<{ status: string; captions_status: VideoCaptionsStatus }> {
  return fetchApi(
    `/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/captions/retry`,
    { method: 'POST', headers: getAuthHeaders() },
  );
}

/** Render EN+ES .vtt from the reviewed cues to the public bucket, point the row at
 *  them, and publish (`published=TRUE`). Requires captions_status='ready' (409
 *  otherwise). Audited update — the "history row on publish" the editor promises. */
export async function publishVideoItem(
  videoItemId: string,
): Promise<{ status: string }> {
  return fetchApi(`/api/admin/learn-content/video-items/${encodeURIComponent(videoItemId)}/publish`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// feat-166: reading item (markdown body + optional ordered images)
// ============================================================

/** The row the editor tree renders for a reading item (from `get_quiz_content` →
 *  `lesson.reading_items`). `body` is the raw markdown SOURCE; `image_s3_keys` is
 *  the ordered list of public-bucket keys, and `image_urls` is the backend-built
 *  convenience list of public URLs (same order) for the editor preview. Reading
 *  items grant NO XP and have no async status — served as soon as `archived=0`. */
export interface ReadingItemSummary {
  id: string;
  /** feat-198: was `lesson_id`. `reading_item` is NOT renamed — only this column. */
  drill_id: string | null;
  title: string;
  /** Raw markdown source text (v1 renders plain paragraphs on mobile). */
  body: string;
  sort_order: number;
  /** Ordered public-bucket S3 keys (`lesson-images/<tenant>/<lesson>/<uuid>.<ext>`);
   *  render order = array order. `[]` = no images. */
  image_s3_keys: string[];
  /** Public URLs for `image_s3_keys` (same order), built by the backend. */
  image_urls: string[];
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;
  /** feat-252: server-owned last-modified timestamp, ISO-8601 UTC (`...Z`). Only
   *  the three tables that carry an `updated` column emit this. Optional and
   *  read-only, same as {@link created}. */
  updated?: string;

  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this reading is entirely untranslated. */
  translations?: TranslationOverlay<ReadingTranslatableField>;
}

/**
 * Step 1 of a reading-item image upload: ask the backend (admin-gated, tenant-
 * scoped) for a presigned POST policy. Reuses feat-165's `_presigned_upload`
 * helper + the existing `lesson-images/*` prefix + content-type allowlist (2 MB
 * png/jpeg/webp). The server builds the key from validated parts; `filename` is
 * advisory only (never used in the key). Same presign surface as the Lesson image
 * uploader, keyed under the lesson instead of the unit.
 */
export async function requestReadingImageUploadUrl(
  lessonId: string,
  body: { tenant_id: string; content_type: string; filename?: string },
): Promise<LessonImageUploadPolicy> {
  return fetchApi(
    `/api/admin/learn-content/drills/${encodeURIComponent(lessonId)}/reading-image-upload-url`,
    { method: 'POST', headers: getAuthHeaders(), body },
  );
}

/**
 * Upload one reading-item image end-to-end: presign → POST(FormData) to S3 →
 * return the key the caller collects (in order) into `image_s3_keys`. Clone of
 * {@link uploadLessonImage}: policy fields FIRST, file LAST, no Content-Type
 * header (browser sets the multipart boundary). S3 replies 204 on success.
 */
export async function uploadReadingImage(
  file: File,
  tenantId: string,
  lessonId: string,
): Promise<{ s3_key: string }> {
  const policy = await requestReadingImageUploadUrl(lessonId, {
    tenant_id: tenantId,
    content_type: file.type,
    filename: file.name,
  });

  const form = new FormData();
  // Policy fields FIRST...
  for (const [k, v] of Object.entries(policy.upload.fields)) {
    form.append(k, v);
  }
  // ...file LAST (S3 requirement).
  form.append('file', file);

  const res = await fetch(policy.upload.url, { method: 'POST', body: form });
  if (!res.ok) {
    throw new Error(`Image upload failed: ${res.status} ${res.statusText}`);
  }
  return { s3_key: policy.s3_key };
}

/**
 * Create a reading item under a drill (lesson). The backend validates any supplied
 * `image_s3_keys` against the tenant's `lesson-images/` prefix, inserts the row,
 * audits the create (feat-164), and enqueues a translation (feat-018). Returns the
 * new id. `body` is the raw markdown source (required, non-empty).
 */
export async function createReadingItem(
  lessonId: string,
  data: {
    tenant_id: string;
    title?: string | null;
    body: string;
    sort_order?: number;
    image_s3_keys?: string[];
  },
): Promise<{ id: string }> {
  return fetchApi(
    `/api/admin/learn-content/drills/${encodeURIComponent(lessonId)}/reading-items`,
    { method: 'POST', headers: getAuthHeaders(), body: data },
  );
}

/** Edit a reading item's title / body / sort_order / ordered images (audited
 *  update). Only the sent fields change; passing `image_s3_keys` replaces the
 *  whole ordered list. A title/body change re-enqueues the translation. */
export async function updateReadingItem(
  readingItemId: string,
  data: Partial<{
    title: string | null;
    body: string;
    sort_order: number;
    image_s3_keys: string[];
  }>,
): Promise<{ status: string }> {
  return fetchApi(`/api/admin/learn-content/reading-items/${encodeURIComponent(readingItemId)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

/** Soft-delete (archived=TRUE) so the audit trail keeps its target. Audited delete. */
export async function deleteReadingItem(readingItemId: string): Promise<void> {
  await fetchApi(`/api/admin/learn-content/reading-items/${encodeURIComponent(readingItemId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

export interface QuizUnit {
  id: string;
  title: string;
  /** Optional one-line subtitle shown under the title on the Learn screen.
   *  Canonical English; auto-translates via the feat-018 pipeline. Existing
   *  units start empty (no backfill) — empty renders no subtitle line. */
  subtitle: string;
  emoji: string;
  /** feat-033 (ADR 0001 §OQ-D): key from the frozen {@link UNIT_MASCOT_CATALOG}
   *  picked by the admin; Flutter maps it to the unit-card mascot art. NULL ⇒ no
   *  mascot (existing units render none until backfilled). */
  mascot_key: string | null;
  /** feat-108 (ADR 0023): full S3 object key of an admin-uploaded Lesson image in
   *  the public-read `bespoke-public-assets` bucket
   *  (`lesson-images/<tenant>/<unit>/<uuid12>.<ext>`). When set it takes
   *  precedence over `mascot_key`/`emoji` in BOTH the web preview and the Flutter
   *  unit card (precedence: image → mascot → emoji). NULL ⇒ fall back to the
   *  mascot. Resolve to a URL via {@link lessonImagePublicUrl}. Not translatable. */
  image_s3_key: string | null;
  sort_order: number;
  active: boolean;
  /** feat-198 (ADR 0001 §D-P.2): was `lessons`. A level-1 Lesson's children are
   *  Drills — the container key that flipped MEANING, not just spelling. */
  drills: QuizLesson[];
  /** feat-252: server-owned creation timestamp, ISO-8601 UTC (`...Z`). Optional
   *  so an older backend (and every pre-feat-252 test fixture) still type-checks
   *  — the `video_items?` precedent. Read-only: never send it back. */
  created?: string;
  /** feat-256: optional release instant, ISO-8601 UTC (`...Z`), or NULL for
   *  "available as soon as it is Active" — where all 41 pre-feature Lessons sit.
   *
   *  INDEPENDENT of {@link QuizUnit.active}, and that pairing IS the feature: a
   *  learner may see the Lesson when `active && (available_at == null ||
   *  available_at <= now)`. A Hidden Lesson RETAINS its date (inert, not
   *  cleared), so flipping back to Active restores the schedule.
   *
   *  NOT gated on the admin read: `GET /api/tenant/{id}/learn-content` returns
   *  it on EVERY Lesson including Hidden and scheduled-future ones, which is
   *  what lets the authoring UI render the schedule at all (spec AC17).
   *
   *  Optional for the same reason `created` is — an older backend and every
   *  pre-feat-256 fixture still type-check (the `video_items?` precedent). */
  available_at?: string | null;

  /** feat-303-curriculum-drip-release: the Lesson bypasses drip release — every
   *  learner gets it as soon as the other gates allow, and it neither sits in
   *  the drip sequence nor consumes allowance. Written via `updateUnit` with a
   *  real boolean (the backend 422s an explicit null). Optional so an older
   *  backend and pre-feat-303 fixtures still type-check; absent reads as false. */
  drip_bypass?: boolean;

  /** feat-284: additive translation overlay, present only when the read was
   *  made with `?lang=`. Absent key ⇒ this unit is entirely untranslated. */
  translations?: TranslationOverlay<UnitTranslatableField>;

  /** feat-302 (ADR 0001 §10.2): who this Lesson is shown to. Always present on
   *  a feat-302 backend (everyone/everyone when unscoped). Optional so an older
   *  backend and every pre-feat-302 fixture still type-check; read it through
   *  {@link audienceOf}, which treats a missing value as everyone/everyone. */
  audience?: Audience;

  /** feat-354-auto-copy-lessons (CONTRACT C2): set when this Lesson is an
   *  auto-copy — the ROOT original's id (never another copy). null/absent ⇒ an
   *  original. There is no FK: a deleted original leaves the id dangling. */
  copied_from_lesson_id?: string | null;
  /** feat-354 (CONTRACT C2): the original's current canonical English title.
   *  null when this is an original OR when the original has been deleted —
   *  read it together with {@link QuizUnit.copied_from_lesson_id}. */
  copied_from_lesson_title?: string | null;
}

export interface QuizContent {
  tenant_id: string;
  /** feat-198 (ADR 0001 §D-P.2): was `units`. */
  lessons: QuizUnit[];
}

// ============================================================
// Frozen authoring catalogs (feat-033, ADR 0001 §OQ-D)
// ============================================================

export interface CatalogEntry {
  /** Stored value (matches the Flutter icon/mascot map key 1:1). */
  key: string;
  /** Admin-facing label (English-only — admin strings are not localized). */
  label: string;
}

/**
 * Lesson-path icon catalog — FROZEN in ADR 0001 §OQ-D (12 keys). The admin
 * picks one of these per lesson; Flutter ships a matching icon for each key.
 * Order here is the dropdown order. Keys must stay in lockstep with the Flutter
 * `iconKey` map — do not add/rename without a cross-app contract change.
 */
export const LESSON_ICON_CATALOG: ReadonlyArray<CatalogEntry> = [
  { key: 'chat_bubble', label: 'Chat' },
  { key: 'microphone', label: 'Mic' },
  { key: 'text_t', label: 'Text' },
  { key: 'star', label: 'Star' },
  { key: 'book', label: 'Book' },
  { key: 'utensils', label: 'Utensils' },
  { key: 'lightbulb', label: 'Idea' },
  { key: 'trophy', label: 'Trophy' },
  { key: 'target', label: 'Target' },
  { key: 'heart', label: 'Heart' },
  { key: 'wine_glass', label: 'Drink' },
  { key: 'checklist', label: 'Checklist' },
];

/**
 * Unit mascot catalog — FROZEN in ADR 0020, stamped at TEN keys: the original 3
 * prototype-pilot mascots (elephant/teacup/sandwich) plus 7 unit mascots added
 * for feat-103. ADR 0020 supersedes ADR 0001 Amendment A3's 3-key cap. The admin
 * picks one per unit; Flutter bundles matching art under `assets/unit_mascot/<key>`
 * (distinct from the existing `assets/mascot/` Benny emotion sheet) and derives the
 * per-unit gradient from `mascot_key`. Keys must stay in lockstep with the Flutter
 * `mascotKey` map / `kUnitMascotKeys` — extending this is a cross-app contract
 * change (new key + new art land together). Single editable array so a re-freeze
 * is a one-line swap.
 */
export const UNIT_MASCOT_CATALOG: ReadonlyArray<CatalogEntry> = [
  { key: 'elephant', label: 'Elephant' },
  { key: 'teacup', label: 'Teacup' },
  { key: 'sandwich', label: 'Sandwich' },
  { key: 'perfect_pairing', label: 'Perfect Pairing' },
  { key: 'personalized_upsells', label: 'Personalized Upselling' },
  { key: 'handling_no_thanks', label: 'Handling "No Thanks"' },
  { key: 'safety_sanitation', label: 'Safety & Sanitation' },
  { key: 'portion_control', label: 'Portion Control' },
  { key: 'daily_special', label: 'Daily Special' },
  { key: 'respect_community', label: 'Respect & Community' },
];

// ============================================================
// API
// ============================================================

export async function listTenants(): Promise<Array<{ tenant_id: string; tenant_name: string }>> {
  return fetchApi<Array<{ tenant_id: string; tenant_name: string }>>(
    '/api/tenant',
    { headers: getAuthHeaders() }
  );
}

/**
 * Read the whole curriculum tree for a tenant.
 *
 * feat-284 adds the optional `lang`. **With `lang` omitted, `''` or `'en'` the
 * request and the response are byte-identical to before** — `QuizContentManagement.tsx`
 * (the editor) reads this same endpoint and must observe no change at all.
 *
 * ⚠️ `lang` IS A SINGLE LANGUAGE CODE, NEVER A BROWSER `?lang=` VALUE.
 *
 * `/quiz-review`'s URL carries `en,es` for Both mode. The backend's
 * `normalize_lang()` (`services/content_translation.py:235`) is Accept-Language
 * shaped: it takes the FIRST comma-separated token and strips the region subtag,
 * so `normalize_lang('en,es') === 'en'`. Forwarding `en,es` here therefore asks
 * for ENGLISH, gets no overlay back, and renders two identical English columns —
 * with no error, no 4xx and nothing in the console. That is the one failure in
 * this feature that is completely silent, so this function refuses it LOUDLY
 * instead: a comma throws. Both mode and ES mode make the SAME request
 * (`lang=es`) and differ only in how the one payload is rendered.
 *
 * @param lang a single non-English code (`'es'`). Omit/`'en'` for canonical English.
 */
export async function getQuizContent(tenantId: string, lang?: string): Promise<QuizContent> {
  if (lang && lang.includes(',')) {
    throw new Error(
      `getQuizContent: 'lang' must be a single language code, got "${lang}". ` +
        'The browser ?lang= value (e.g. "en,es" for Both mode) is NOT the API ' +
        'value — pass the selected non-English code instead.',
    );
  }
  const normalized = lang?.trim().toLowerCase();
  // `en` sends NO parameter rather than `lang=en`: identical semantics on the
  // backend, but it keeps the editor's request line byte-identical to today's.
  const query =
    normalized && normalized !== 'en' ? `?lang=${encodeURIComponent(normalized)}` : '';
  return fetchApi<QuizContent>(`/api/tenant/${tenantId}/learn-content${query}`, {
    headers: getAuthHeaders(),
  });
}

// --- Units ---

export async function createUnit(
  tenantId: string,
  data: { title: string; subtitle?: string; emoji?: string; mascot_key?: string | null; image_s3_key?: string | null; sort_order?: number; active?: boolean }
): Promise<{ id: string }> {
  return fetchApi(`/api/tenant/${tenantId}/learn-lessons`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

/** Patch a Lesson (`learn_lesson`). Only the sent fields change (the backend's
 *  `exclude_unset` `_apply_update` path), and an explicit `null` CLEARS the
 *  column — feat-187 (D2) relies on that to null `emoji` when a catalog mascot
 *  is picked and vice versa, the same way feat-108 clears `image_s3_key`.
 *
 *  feat-256 rides that same three-way contract for `available_at` (spec AC15):
 *  an ISO-8601 UTC string SETS it, an explicit `null` CLEARS it, and OMITTING
 *  the key leaves it untouched. Callers unscheduling a Lesson must therefore
 *  send `null` — never `undefined` (dropped by `exclude_unset`, a silent no-op)
 *  and never `''`. */
export async function updateUnit(
  unitId: string,
  data: Partial<{ title: string; subtitle: string; emoji: string | null; mascot_key: string | null; image_s3_key: string | null; sort_order: number; active: boolean; available_at: string | null; drip_bypass: boolean }>
): Promise<void> {
  await fetchApi(`/api/learn-lessons/${unitId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteUnit(unitId: string): Promise<void> {
  await fetchApi(`/api/learn-lessons/${unitId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// feat-235 — TRACKS (level 0). Backend: the seven handlers under the
// "feat-235 — TRACKS" banner in routes/admin_quiz_content.py.
//
// A Track sits ABOVE Lesson and is many-to-many with it (program doc D6): one
// Lesson belongs to several Tracks, and each Track holds its OWN ordering of its
// Lessons in `learn_track_lesson.sort_order`. That is why membership is replaced
// as a WHOLE ORDERED LIST rather than patched per row — and why NOTHING here can
// write `learn_lesson.sort_order`, which is the column the mobile payload orders
// by (program-doc invariant 1).
//
// Ids are ULIDs (26 chars, Crockford base32), not UUIDs — `learn_track.id` and
// both columns of `learn_track_prereq`. `lesson_id` stays a 36-char UUID because
// `learn_lesson` mints it. Nothing on this client parses either; they are opaque
// strings on the wire. The mixed width is by design, not an oversight.
// ============================================================

/** Closed set, matching the `access_mode` ENUM in migration 114 and the backend's
 *  `TRACK_ACCESS_MODES`. `granted` is deliberately absent until feature 6 — the
 *  slice-3 unlock evaluator branches on this value, so an unhandled mode is a
 *  track that quietly never unlocks. */
export type TrackAccessMode = 'open' | 'prereq';

export interface LearnTrack {
  /** ULID (CHAR(26)). */
  id: string;
  title: string;
  /** Translatable; renders under the track name on the crew's phone (slice 3). */
  description: string | null;
  access_mode: TrackAccessMode;
  /** In-progress art. Mirrors the `learn_lesson` pair. NO picker ships for
   *  `mascot_key` this slice (design-lead, mockup §9) — the column exists per
   *  D10, the control does not. An art-less track renders a derived monogram. */
  mascot_key: string | null;
  image_s3_key: string | null;
  /** D10 second art state. Nothing renders it until slice 3; it is authored now
   *  because content is authored once (program-doc invariant 4). */
  completed_mascot_key: string | null;
  completed_image_s3_key: string | null;
  sort_order: number;
  active: boolean;
  /** The implicit track migration 114 backfilled for every tenant (D2). It
   *  cannot be deleted — `DELETE` answers 409 — and there is no supported way to
   *  move the flag, so no client ever sends it. */
  is_default: boolean;
  /** This track's membership, already in this track's own order. */
  lesson_ids: string[];
  lesson_count: number;
  prereq_track_ids: string[];

  /** feat-302 (ADR 0001 §10.2): who this Track is shown to. See
   *  {@link QuizUnit.audience} for why it is optional. */
  audience?: Audience;
}

/**
 * Health of a tenant's D2 invariant — "exactly one `is_default = 1` track" —
 * classified server-side by `classify_default_track_state`. FOUR states, because
 * the two missing-a-default cases are different problems:
 *
 *   * `ok`                 — exactly one default. The only normal state.
 *   * `missing_no_tracks`  — no tracks at all. The tenant row never went through
 *     migration 114 or `create_tenant`; this is a provenance question.
 *   * `missing_has_tracks` — has tracks, none default. It went through a
 *     supported path and then the default was removed.
 *   * `multiple_defaults`  — DB-impossible while `uq_learn_track_default` exists.
 *     Reported anyway, because the day it appears is the day that index is gone.
 *
 * THE UI MUST NOT COLLAPSE THESE INTO "empty". A tenant with no default and no
 * tracks renders identically to a brand-new tenant unless this field is read,
 * and the empty state's own call to action then LAUNDERS the fault: creating a
 * track flips the tenant to `missing_has_tracks` (`create_learn_track` hard-codes
 * `is_default = 0`), the empty state stops firing, and because the delete guard
 * keys on `is_default` every track on that tenant becomes deletable — including
 * the one the author believes is the default.
 */
export type DefaultTrackState =
  | 'ok'
  | 'missing_no_tracks'
  | 'missing_has_tracks'
  | 'multiple_defaults';

export interface LearnTracksPayload {
  tenant_id: string;
  tracks: LearnTrack[];
  /** Lessons of this tenant that are in NO track. Server-computed as a set
   *  difference over the whole tenant — do NOT re-derive it from `tracks`, which
   *  is only correct while the client holds every track. Empty for every tenant
   *  immediately after migration 114 (the backfill filed everything into Core). */
  unassigned_lesson_ids: string[];
  /** The tenant's default track, or null when there isn't exactly one. */
  default_track_id: string | null;
  /** See {@link DefaultTrackState}. Additive key — a backend that predates it
   *  omits it, which is why every consumer here treats a missing value as `ok`
   *  rather than as a fault. */
  default_track_state: DefaultTrackState;

  /** feat-302 (ADR 0001 §10.2): the tenant's locations and roles, for the
   *  audience chips, the editor's pickers and View as. Absent on an older
   *  backend — the UI treats absence as "could not load", never as empty. */
  audience_options?: AudienceOptions;
}

/** Everything the Tracks tab renders, in one call. */
export async function getLearnTracks(tenantId: string): Promise<LearnTracksPayload> {
  return fetchApi<LearnTracksPayload>(`/api/tenant/${tenantId}/learn-tracks`, {
    headers: getAuthHeaders(),
  });
}

export async function createTrack(
  tenantId: string,
  data: {
    title: string;
    description?: string | null;
    access_mode?: TrackAccessMode;
    mascot_key?: string | null;
    image_s3_key?: string | null;
    completed_mascot_key?: string | null;
    completed_image_s3_key?: string | null;
    sort_order?: number;
    active?: boolean;
  },
): Promise<{ id: string }> {
  return fetchApi(`/api/tenant/${tenantId}/learn-tracks`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

/**
 * Patch a Track. Same `_apply_update` contract as `PUT /api/learn-lessons/{id}`:
 * only the sent fields change and an explicit `null` CLEARS a nullable column —
 * that is how art is removed. The NOT NULL columns (`title`, `access_mode`,
 * `sort_order`, `active`) reject an explicit null with a 422 naming the field.
 *
 * `is_default` is not patchable and is not in this type.
 */
export async function updateTrack(
  trackId: string,
  data: Partial<{
    title: string;
    description: string | null;
    access_mode: TrackAccessMode;
    mascot_key: string | null;
    image_s3_key: string | null;
    completed_mascot_key: string | null;
    completed_image_s3_key: string | null;
    sort_order: number;
    active: boolean;
  }>,
): Promise<void> {
  await fetchApi(`/api/learn-tracks/${trackId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

/** Delete a Track. **409 on the default track** — the UI disables the control
 *  rather than sending a request it knows will be refused. Membership and prereq
 *  rows cascade; `learn_lesson` rows do not (the cascade is on the edge table). */
export async function deleteTrack(trackId: string): Promise<void> {
  await fetchApi(`/api/learn-tracks/${trackId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

/**
 * Replace a Track's WHOLE ordered membership list. `sort_order` is the array
 * index, so ordering is expressed by sending the order — an add, a drag and a
 * remove are all the same request.
 *
 * feat-243 — OPTIMISTIC CONCURRENCY. `expectedLessonIds` is the list this client
 * believes it is overwriting, and it is **required**, not optional (spec.md R4:
 * an optional check is one nobody sends, and it would be omitted by exactly the
 * stale client that most needs it — the server answers a body without it with a
 * 422). The server compares it, under a row lock, against what is actually
 * stored and answers **409** with a structured `detail` naming what diverged and
 * who changed it (spec.md §3a) rather than silently clobbering the other write.
 *
 * **Pass the list as LOADED, never one recomputed from the edit being submitted**
 * (AC19) — a recomputed value always matches, which makes the check vacuous.
 * Lessons compare as an ORDERED SEQUENCE: order is the payload here.
 */
export async function replaceTrackLessons(
  trackId: string,
  lessonIds: string[],
  expectedLessonIds: string[],
): Promise<{ status: string; lesson_count: number }> {
  return fetchApi(`/api/learn-tracks/${trackId}/lessons`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: { lesson_ids: lessonIds, expected_lesson_ids: expectedLessonIds },
  });
}

/**
 * Replace a Track's WHOLE prerequisite list. Semantics are **AND** — every
 * listed track must be complete. The server rejects self-reference, cycles at
 * any depth, and cross-tenant ids with a 400; the picker disables those options
 * so the rejection is normally shown rather than reached.
 *
 * feat-243 — `expectedRequiredTrackIds` is the same optimistic-concurrency check
 * as {@link replaceTrackLessons} and is likewise **required**. Prereqs compare as
 * a **SET** on the server, so picker order alone can never 409.
 */
export async function replaceTrackPrereqs(
  trackId: string,
  requiredTrackIds: string[],
  expectedRequiredTrackIds: string[],
): Promise<{ status: string; prereq_count: number }> {
  return fetchApi(`/api/learn-tracks/${trackId}/prereqs`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: {
      required_track_ids: requiredTrackIds,
      expected_required_track_ids: expectedRequiredTrackIds,
    },
  });
}

/** Presign a Track art upload. Same policy shape, bucket, type allowlist and
 *  2 MB ceiling as the Lesson uploader — only the key prefix differs
 *  (`track-images/…`). Either art state attaches the returned key afterwards via
 *  {@link updateTrack}. */
export async function requestTrackImageUploadUrl(body: {
  tenant_id: string;
  track_id: string;
  content_type: string;
  filename?: string;
}): Promise<LessonImageUploadPolicy> {
  return fetchApi('/api/admin/learn-content/tracks/image-upload-url', {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });
}

/**
 * Upload one Track art file end-to-end: presign → POST(FormData) to S3 → return
 * the key the caller persists via {@link updateTrack} on EITHER `image_s3_key`
 * (in-progress) or `completed_image_s3_key` (D10 completed state). Same presigned
 * **POST** mechanics as {@link uploadLessonImage}: policy fields first, file last,
 * no Content-Type header, S3 answers 204.
 */
export async function uploadTrackImage(
  file: File,
  tenantId: string,
  trackId: string,
): Promise<{ s3_key: string }> {
  const policy = await requestTrackImageUploadUrl({
    tenant_id: tenantId,
    track_id: trackId,
    content_type: file.type,
    filename: file.name,
  });

  const form = new FormData();
  for (const [k, v] of Object.entries(policy.upload.fields)) {
    form.append(k, v);
  }
  form.append('file', file);

  const res = await fetch(policy.upload.url, { method: 'POST', body: form });
  if (!res.ok) {
    throw new Error(`Image upload failed: ${res.status} ${res.statusText}`);
  }
  return { s3_key: policy.s3_key };
}

/** Resolve a Track art key to its public URL. Same public-read bucket and same
 *  no-signing read path as a Lesson image; separate name so a call site says
 *  which entity it is resolving. */
export function trackImagePublicUrl(s3Key: string | null | undefined): string | null {
  return lessonImagePublicUrl(s3Key);
}

// --- Lesson (unit) custom image (feat-108, ADR 0023) ---

/**
 * Public-read bucket that hosts Lesson images (and feat-103.1 mascot art). A
 * Lesson's `image_s3_key` is a full object key under this bucket; the read path
 * (web preview + Flutter) is a plain HTTPS URL with NO signing — the bucket
 * serves reads via its public-read policy. Resolve a key with
 * {@link lessonImagePublicUrl}.
 */
export const PUBLIC_ASSETS_BASE_URL =
  'https://bespoke-public-assets.s3.us-west-1.amazonaws.com';

/** Content types the Lesson-image uploader accepts. S3 hard-enforces this exact
 *  set as a presigned-POST condition; the client pre-checks only for a friendly
 *  message (ADR 0023 §D1). SVG/HTML are intentionally excluded (stored-XSS). */
export const LESSON_IMAGE_ALLOWED_TYPES: ReadonlyArray<string> = [
  'image/png',
  'image/jpeg',
  'image/webp',
];

/** Max Lesson-image size — 2 MB. S3 is the authority (`content-length-range`);
 *  the client pre-check just avoids a wasted round-trip + gives a clear message. */
export const LESSON_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/** Resolve a Lesson `image_s3_key` to its public URL. NULL/empty ⇒ null. */
export function lessonImagePublicUrl(s3Key: string | null | undefined): string | null {
  if (!s3Key) return null;
  return `${PUBLIC_ASSETS_BASE_URL}/${s3Key}`;
}

/** The presigned-POST policy the backend mints (ADR 0023 §D1). `upload.fields`
 *  must be appended to the FormData FIRST and the file LAST (S3 requires the
 *  file last). S3 returns HTTP 204 on a successful POST. */
export interface LessonImageUploadPolicy {
  s3_key: string;
  upload: { url: string; fields: Record<string, string> };
  expires_in: number;
}

/**
 * Step 1 of the Lesson-image upload: ask the backend (admin-gated, tenant-scoped)
 * for a presigned POST policy. Server builds the key from validated parts; the
 * `filename` is advisory only (ignored server-side). The presign endpoint hard-
 * enforces the content-type allowlist and 2 MB cap as S3 POST conditions.
 */
export async function requestLessonImageUploadUrl(body: {
  tenant_id: string;
  /** feat-198 (ADR 0001 §D-P.4): was `unit_id`. This names the level-1 entity's
   *  OWN id on a lesson endpoint, so `lesson_id` is unambiguous here —
   *  `parent_lesson_id` is only for a pointer UP from a drill. */
  lesson_id: string;
  content_type: string;
  filename?: string;
}): Promise<LessonImageUploadPolicy> {
  return fetchApi('/api/admin/learn-content/lessons/image-upload-url', {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });
}

/**
 * Upload a Lesson image end-to-end: presign → POST(FormData) to S3 → return the
 * key the caller persists via {@link updateUnit}({ image_s3_key }).
 *
 * NB this is a presigned **POST** (multipart/form-data), NOT the raw-PUT shape
 * `rehearsalAdminService.uploadScenarioImage` uses — S3 enforces type+size from
 * the policy fields. The file MUST be appended last, and we set no Content-Type
 * header (the browser supplies the multipart boundary). S3 replies 204 on success.
 */
export async function uploadLessonImage(
  file: File,
  tenantId: string,
  unitId: string,
): Promise<{ s3_key: string }> {
  const policy = await requestLessonImageUploadUrl({
    tenant_id: tenantId,
    lesson_id: unitId,
    content_type: file.type,
    filename: file.name,
  });

  const form = new FormData();
  // Policy fields FIRST...
  for (const [k, v] of Object.entries(policy.upload.fields)) {
    form.append(k, v);
  }
  // ...file LAST (S3 requirement).
  form.append('file', file);

  const res = await fetch(policy.upload.url, { method: 'POST', body: form });
  if (!res.ok) {
    throw new Error(`Image upload failed: ${res.status} ${res.statusText}`);
  }
  return { s3_key: policy.s3_key };
}

// --- Lessons ---

export async function createLesson(
  unitId: string,
  data: {
    title: string;
    emoji?: string;
    icon_key?: string | null;
    xp_reward?: number;
    question_count?: number | null;
    sort_order?: number;
    active?: boolean;
  }
): Promise<{ id: string }> {
  return fetchApi(`/api/learn-lessons/${unitId}/drills`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updateLesson(
  lessonId: string,
  data: Partial<{
    title: string;
    emoji: string;
    icon_key: string | null;
    xp_reward: number;
    question_count: number | null;
    sort_order: number;
    active: boolean;
  }>
): Promise<void> {
  await fetchApi(`/api/learn-drills/${lessonId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteLesson(lessonId: string): Promise<void> {
  await fetchApi(`/api/learn-drills/${lessonId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

/** feat-361 — response of `POST /api/learn-drills/{id}/move`. */
export interface MoveDrillResult {
  status: string;
  /** The destination Lesson id (== the requested `target_lesson_id`). */
  parent_lesson_id: string;
  /** The drill's new position: max(destination drills' sort_order) + 1, or 0. */
  sort_order: number;
}

/**
 * feat-361 — move a Drill to another Lesson in the same tenant. The backend
 * appends it LAST in the destination and logs the change to curriculum History.
 * Everything under the drill (questions, rehearsals, videos, readings) follows it
 * because it hangs off the unchanged drill id.
 *
 * Errors: 400 (same lesson / cross-tenant), 403 (no access to the drill's
 * tenant), 404 (drill or lesson missing). Thrown as `ApiError` by `fetchApi`.
 */
export async function moveDrill(drillId: string, targetLessonId: string): Promise<MoveDrillResult> {
  return fetchApi<MoveDrillResult>(`/api/learn-drills/${drillId}/move`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: { target_lesson_id: targetLessonId },
  });
}

// --- Questions ---

export async function createQuestion(
  lessonId: string,
  data: {
    question_type?: QuestionType;
    question_text: string;
    option_a: string;
    option_b: string;
    option_c: string;
    option_d: string;
    correct_option: CorrectOption;
    explanation?: string;
    correct_explanation?: string;
    xp_value?: number | null;
    /** feat-075: newline-delimited accepted variants (fill-blank only). NULL ⇒
     *  no variants. Server normalizes/validates; the UI sends the raw string. */
    blank_accepted_variants?: string | null;
    /** feat-325: null ⇒ Auto. Absent in a PUT ⇒ unchanged (CONTRACT §2). */
    blank_grading_mode?: BlankGradingMode | null;
    sort_order?: number;
  }
): Promise<{ id: string }> {
  return fetchApi(`/api/learn-drills/${lessonId}/question-items`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updateQuestion(
  questionId: string,
  data: Partial<{
    question_type: QuestionType;
    question_text: string;
    option_a: string;
    option_b: string;
    option_c: string;
    option_d: string;
    correct_option: CorrectOption;
    explanation: string;
    correct_explanation: string;
    xp_value: number | null;
    /** feat-075: newline-delimited accepted variants (fill-blank only). NULL ⇒
     *  no variants. Server normalizes/validates; the UI sends the raw string. */
    blank_accepted_variants: string | null;
    /** feat-325: null ⇒ Auto. Absent in a PUT ⇒ unchanged (CONTRACT §2). */
    blank_grading_mode: BlankGradingMode | null;
    sort_order: number;
  }>
): Promise<void> {
  await fetchApi(`/api/question-items/${questionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteQuestion(questionId: string): Promise<void> {
  await fetchApi(`/api/question-items/${questionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// --- feat-325: fill-blank "Try an answer" (CONTRACT §3) ---

export type BlankTestLocale = 'en' | 'es';

/** `graded`: the grader answered. `deterministic_match: true` ⇒ no model call
 *  was made (exact canonical/variant match); `reason` is then "Exact match".
 *  `fallback`: model error / timeout — the phone would use its deterministic
 *  result. `error` is the backend's short code, shown as-is. */
export type BlankTestGradeResult =
  | {
      status: 'graded';
      correct: boolean;
      reason: string;
      mode_applied: BlankGradingMode;
      deterministic_match: boolean;
      latency_ms: number | null;
    }
  | { status: 'fallback'; error: string };

/** Grade one answer through the same service learners hit, as an editor test
 *  (source = editor_test; no XP, no attempt row). `blank_grading_mode` is ALWAYS
 *  sent (null = Auto) so an unsaved toggle is what gets tested — an absent key
 *  would mean "use the stored value" (CONTRACT §3). */
export async function testGradeBlank(
  questionId: string,
  body: { answer: string; locale: BlankTestLocale; blank_grading_mode: BlankGradingMode | null },
): Promise<BlankTestGradeResult> {
  return fetchApi(`/api/admin/question-items/${questionId}/test-grade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });
}

// --- Rehearsals (inline lesson items: scenario + criteria + XP) ---

export async function createRehearsal(
  lessonId: string,
  data: {
    role: string;
    name: string;
    scenario_text: string;
    scenario_image_s3_key?: string | null;
    location_label?: string | null;
    correct_example_response?: string | null;
    xp_value?: number;
    redo_xp?: number;
    fail_xp?: number;
    max_recording_seconds?: number;
    sort_order?: number;
    criteria: RehearsalCriterion[];
  },
): Promise<RehearsalLessonItem> {
  return fetchApi(`/api/learn-drills/${lessonId}/rehearsals`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updateRehearsal(
  rehearsalId: string,
  data: Partial<{
    name: string;
    scenario_text: string;
    scenario_image_s3_key: string | null;
    location_label: string | null;
    correct_example_response: string | null;
    xp_value: number;
    redo_xp: number;
    fail_xp: number;
    max_recording_seconds: number;
    sort_order: number;
    criteria: RehearsalCriterion[];
    repin_prompt_version: boolean;
  }>,
): Promise<RehearsalLessonItem> {
  return fetchApi(`/api/rehearsals/${rehearsalId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteRehearsal(rehearsalId: string): Promise<void> {
  await fetchApi(`/api/rehearsals/${rehearsalId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// feat-078: Rehearsal test harness (admin in-browser grading sandbox)
// ============================================================
//
// Three endpoint families, all under `/api/admin/rehearsals/{id}/...`, all
// gated `require_corporate_manager_or_admin` + tenant-scoped via the parent
// template (ADR 0001 §3 / §6). The grader is a pure text-in → verdict-out
// Claude call shared with the live path (N1); test-grade is STATELESS (N2 —
// zero rehearsal_attempt rows / zero XP). Run-result bookkeeping lives only on
// the suite table (`last_passed`/`last_run_at`/`last_language`), PATCHed after
// a run, so grading itself stays side-effect free.

/** Coaching/praise language for a test grade. Pass/fail is language-agnostic;
 *  the toggle only changes the language of the coaching + praise text (AC6). */
export type RehearsalTestLanguage = 'en' | 'es';

/** Expected verdict an author labels a statement with. `unset` = no expectation
 *  (never flagged as a mismatch). AI generation seeds `pass`/`fail`; the author
 *  can override (spec decision #5). */
export type ExpectedVerdict = 'pass' | 'fail' | 'unset';

/** Whether a statement was authored by hand or seeded by AI generation. */
export type TestStatementSource = 'ai' | 'manual';

/** One per-criterion hit/miss from a grade, echoed with the criterion's authored
 *  `label`/`kind` so the UI can render "good things to hear" vs "bad to avoid"
 *  without re-fetching the rehearsal (ADR 0001 §D3.1 step 6). */
export interface RehearsalCriterionHit {
  criterion_id: string;
  label: string;
  kind: RehearsalCriterionKind;
  hit: boolean;
  /** Verbatim quote from the statement that evidences the hit/miss; null when
   *  the grader gave no quote (e.g. a clean miss). */
  evidence_quote: string | null;
}

/** Result of grading one statement through the real grader (D3.1). STATELESS —
 *  produces no attempt row, awards no XP. `passed === null` is a soft-fail
 *  ("couldn't grade"); `analyzer_error` is non-null iff `passed === null`. */
export interface RehearsalTestGradeResult {
  passed: boolean | null;
  coaching_text: string;
  /** Empty string unless `passed === true`. */
  praise_text: string;
  analyzer_error: string | null;
  criterion_hits: RehearsalCriterionHit[];
}

/** An AI-generated candidate statement (D3.2). Not persisted server-side; the
 *  author curates and saves chosen ones via {@link createTestStatement} with
 *  `source: 'ai'`. The generator mostly returns 'pass'/'fail' but may return
 *  'unset' for a genuinely ambiguous edge case. */
export interface GeneratedTestStatement {
  statement: string;
  expected_verdict: ExpectedVerdict;
  rationale?: string | null;
}

/** A persisted suite statement (D3.3). `last_*` capture the most recent run
 *  result so the suite doubles as a regression set (spec decision #3, AC8). */
export interface RehearsalTestStatement {
  id: string;
  rehearsal_template_id: string;
  statement: string;
  expected_verdict: ExpectedVerdict;
  source: TestStatementSource;
  last_passed: boolean | null;
  last_run_at: string | null;
  last_language: string | null;
  position: number;
}

// --- D3.1: stateless grade (Run-all + per-row re-run share this) ---

/** Grade one statement against the SAVED rehearsal's pinned prompt version,
 *  through the same code path as live grading (N1). No persistence (N2). */
export async function testGradeRehearsal(
  rehearsalId: string,
  body: { statement: string; language: RehearsalTestLanguage },
): Promise<RehearsalTestGradeResult> {
  return fetchApi(`/api/admin/rehearsals/${rehearsalId}/test-grade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });
}

// --- D3.2: AI generation (strong/weak/edge spread) ---

/** Ask the AI to propose a labeled strong-pass / clear-fail / edge mix of
 *  statements a real server might say (spec decision #4). Returns candidates
 *  only — nothing is saved until the author calls {@link createTestStatement}. */
export async function generateTestStatements(
  rehearsalId: string,
  body?: { count?: number },
): Promise<GeneratedTestStatement[]> {
  const res = await fetchApi<{ statements: GeneratedTestStatement[] }>(
    `/api/admin/rehearsals/${rehearsalId}/test-generate`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: body ?? {},
    },
  );
  return res.statements;
}

// --- D3.3: suite CRUD + reorder (tenant-scoped via parent rehearsal) ---

export async function listTestStatements(
  rehearsalId: string,
): Promise<RehearsalTestStatement[]> {
  const res = await fetchApi<{ statements: RehearsalTestStatement[] }>(
    `/api/admin/rehearsals/${rehearsalId}/test-statements`,
    { headers: getAuthHeaders() },
  );
  return res.statements;
}

export async function createTestStatement(
  rehearsalId: string,
  data: {
    statement: string;
    expected_verdict?: ExpectedVerdict;
    source?: TestStatementSource;
    position?: number;
  },
): Promise<RehearsalTestStatement> {
  return fetchApi(`/api/admin/rehearsals/${rehearsalId}/test-statements`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updateTestStatement(
  rehearsalId: string,
  statementId: string,
  // Note: `last_run_at` is NOT a client input — the server auto-stamps it to
  // NOW() whenever `last_passed` is PATCHed (AC8). Send `last_passed` (+
  // `last_language`) after a run and read `last_run_at` back off the returned
  // row.
  data: Partial<{
    statement: string;
    expected_verdict: ExpectedVerdict;
    position: number;
    last_passed: boolean | null;
    last_language: string | null;
  }>,
): Promise<RehearsalTestStatement> {
  return fetchApi(
    `/api/admin/rehearsals/${rehearsalId}/test-statements/${statementId}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: data,
    },
  );
}

export async function deleteTestStatement(
  rehearsalId: string,
  statementId: string,
): Promise<void> {
  await fetchApi(
    `/api/admin/rehearsals/${rehearsalId}/test-statements/${statementId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  );
}

export async function reorderTestStatements(
  rehearsalId: string,
  orderedIds: string[],
): Promise<void> {
  await fetchApi(
    `/api/admin/rehearsals/${rehearsalId}/test-statements/reorder`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: { ordered_ids: orderedIds },
    },
  );
}

// ============================================================
// feat-110: Rehearsal attempts history (read-only analytics)
// ============================================================
//
// One real, SAVED `rehearsal_attempt` row — a live user's actual training run
// on this drill (distinct from the stateless test-harness sandbox above, which
// never persists). Served by the same admin family
// (`GET /api/admin/rehearsals/{id}/attempts`, gated
// `require_corporate_manager_or_admin`, tenant-scoped via the parent template).
// The endpoint returns up to the 200 most-recent rows newest-first; the UI caps
// at 10 client-side (spec §2) and renders ONLY the human-readable fields —
// never the ids (spec decision #5: no ID clutter).

/** One persisted attempt. The full backend shape is typed (nothing omitted),
 *  but the "Recent Attempts" UI renders only `started` (timestamp),
 *  `passed` (verdict badge), `transcript_text` (what was said),
 *  `ai_coaching_text` / `analyzer_error` (feedback), and optionally
 *  `ai_praise_text`. `passed`: `true`=pass, `false`=fail, `null`=soft-fail
 *  ("couldn't grade"; `analyzer_error` non-null). `started`/`ended` may be null
 *  on incomplete rows. `ai_praise_text` is `""` unless the attempt passed. */
export interface RehearsalAttempt {
  id: string;
  management_id: string;
  attempt_number: number;
  started: string | null;
  ended: string | null;
  passed: boolean | null;
  xp_awarded: number;
  transcript_text: string;
  ai_coaching_text: string;
  analyzer_error: string | null;
  ai_praise_text: string;
}

/** feat-110: list the most-recent real attempts on a rehearsal across ALL users
 *  (no `management_id` filter ⇒ everyone). Returns the parsed attempts array
 *  (newest-first, up to 200); callers `.slice(0, 10)` — never render all 200.
 *  Same `/api/admin/rehearsals/...` prefix + auth-header pattern as the
 *  test-harness calls above (the path is `/api/admin/...`, NOT `/admin/...`). */
export async function listRehearsalAttempts(
  rehearsalId: string,
): Promise<RehearsalAttempt[]> {
  const res = await fetchApi<{ attempts: RehearsalAttempt[] }>(
    `/api/admin/rehearsals/${rehearsalId}/attempts`,
    { headers: getAuthHeaders() },
  );
  return res.attempts;
}

// ============================================================
// feat-164: Curriculum change history + per-item undo
// ============================================================
//
// Every create/update/delete/revert flowing through the 8-handler chokepoint in
// routes/admin_quiz_content.py is recorded in `curriculum_change_history` with a
// whole-item before/after snapshot (ADR 0001). These three calls expose the
// read + revert surface. Everything is tenant-scoped server-side; a cross-tenant
// view or revert fails closed with 404 (ADR 0001 §D7 — never a 403 that leaks
// existence). Paths are `/api/...` to match the backend router prefix.
//
// `entity_type` is the BACKEND/DB TABLE NAME. It is not a display label and it
// is not derivable from one. Map by data structure:
//   QuizUnit            (UI "Lesson")    → 'learn_lesson'
//   QuizLesson          (UI "Drill")     → 'learn_drill'
//   QuizQuestion        (UI "Question")  → 'question_item'
//   RehearsalLessonItem (UI "Rehearsal") → 'rehearsal_template'
// Use {@link CURRICULUM_ENTITY_TYPE} so this mapping lives in exactly one place.
//
// feat-198 (ADR 0001 §D-P.3) renamed the first three. THESE ARE STORED VALUES,
// not just wire strings — migration 110 UPDATEs the rows in
// `curriculum_change_history.entity_type` and `content_translation.entity_type`
// to match. A client/server disagreement here therefore does NOT throw: the
// query matches zero rows and the history panel and translation queue both
// render EMPTY at 200 OK.
//
// TWO TRAPS, both live:
//  1. THE PREFIX IS NOT UNIFORM. `question_item` takes no `learn_` prefix while
//     `learn_lesson` and `learn_drill` do. A mechanical prefix rewrite produces
//     a well-formed WRONG name, and a wrong one is indistinguishable from a
//     right one at every layer except the row count.
//  2. `video_item` and `reading_item` are UNCHANGED. Their tables were never
//     renamed — only their API paths moved.
//
// The pre-feat-198 values are deliberately NOT written here. They are dead
// strings, they belong to git history and ADR 0001, and naming them beside the
// live ones is the "correction appended under the claim" shape §D-D.3 rejects —
// it also puts them back into AC7's census, which is a zero-result gate.

/** DB `entity_type` values, string-open so feat-165 (video) / feat-166 (reading)
 *  item types read for free without a client change. */
export type CurriculumEntityType =
  | 'learn_track' // feat-235 lvl 0 — UI "Track"
  | 'learn_track_lesson' // feat-235 — a track's membership list (capture only)
  | 'learn_track_prereq' // feat-235 — a track's prerequisite list (capture only)
  | 'learn_lesson' // lvl 1 — UI "Lesson"
  | 'learn_drill' // lvl 2 — UI "Drill"
  | 'question_item' // lvl 3 — UI "Question". NO learn_ prefix; see trap 1.
  | 'rehearsal_template'
  | 'video_item' // feat-165 — table not renamed
  | 'reading_item'; // feat-166 — table not renamed

export type CurriculumChangeAction = 'create' | 'update' | 'delete' | 'revert';

export interface CurriculumHistoryEntry {
  id: string;
  entity_type: CurriculumEntityType | string; // string-open for feat-165/166
  entity_id: string;
  action: CurriculumChangeAction;
  actor_email: string;
  actor_role: string;
  created_at: string; // ISO-8601 UTC
  /** Server-computed: true iff this entry has a restorable prior state (an
   *  update/delete, or a field-revert). Create entries and delete-restores are
   *  non-revertable (ADR 0001 §D5). Trust this flag; do not re-derive it. */
  revertable: boolean;
  /** For action='revert': the history id this revert restored from. */
  reverted_from_id: string | null;
  /** Optional server one-liner, e.g. "Edited question text". */
  summary?: string;
}

/**
 * Per-item change history (newest-first), for a single curriculum item. The
 * backend resolves the tenant from the history rows' stored `tenant_id` (never a
 * live-curriculum join), so a DELETED item's history is still readable here —
 * which is how a delete is found and restored from the item's own view. A
 * foreign-tenant or unknown item yields 404 (fetchApi throws).
 *
 * @param entityType the DB `entity_type` (see {@link CurriculumEntityType}) —
 *   NOT the UI label. Map via {@link CURRICULUM_ENTITY_TYPE}.
 */
export async function getItemHistory(
  entityType: CurriculumEntityType,
  entityId: string,
): Promise<{ entries: CurriculumHistoryEntry[] }> {
  return fetchApi<{ entries: CurriculumHistoryEntry[] }>(
    `/api/learn-content/history?entity_type=${encodeURIComponent(entityType)}` +
      `&entity_id=${encodeURIComponent(entityId)}`,
    { headers: getAuthHeaders() },
  );
}

/**
 * Tenant-wide recent-change feed (newest-first). This is the primary way to find
 * and restore a DELETED item — once its parent row is gone from the tree there is
 * no per-item affordance to open, but the delete entry surfaces here and its
 * Revert restores it in place. Tenant-scoped + IDOR-fail-closed (404) server-side.
 */
export async function getTenantHistory(
  tenantId: string,
  opts?: { limit?: number; before?: string },
): Promise<{ entries: CurriculumHistoryEntry[] }> {
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  if (opts?.before) params.set('before', opts.before);
  const qs = params.toString();
  return fetchApi<{ entries: CurriculumHistoryEntry[] }>(
    `/api/tenant/${encodeURIComponent(tenantId)}/learn-content/history${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() },
  );
}

/**
 * Revert a single history entry — either restore an edited item's prior field
 * values or restore a deleted item in place. The revert is itself recorded as a
 * new history entry (auditable + re-revertible). Item-scoped: only the target
 * row is written; siblings and any later edits to OTHER items are untouched
 * (AT5). On failure fetchApi throws with the backend `detail`:
 *   - 404 → not found / foreign tenant (indistinguishable by design, §D7)
 *   - 409 → nothing to revert to (a create) or the item was independently
 *           hard-deleted after this edit (restore the deletion instead).
 * After a success, the caller MUST re-fetch getQuizContent(tenantId) so the tree
 * reflects the restored/reverted state.
 */
export async function revertCurriculumChange(
  historyId: string,
): Promise<{ status: string; entity_type: string; entity_id: string; action: string }> {
  return fetchApi(`/api/learn-content/history/${encodeURIComponent(historyId)}/revert`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// feat-167: single-entry before/after snapshot read
// ============================================================

/**
 * One side of a change — the whole-item snapshot feat-164 already stores in
 * `before_json` / `after_json`. Deliberately untyped beyond "an object": the
 * blob is a raw `SELECT *` of the row as it existed, so its keys are DB COLUMN
 * names and they drift as columns are added (feat-165 video, feat-166 reading,
 * and whatever comes next). The renderer treats unknown keys generically rather
 * than pinning a field list here — see `normalizeSnapshot` in
 * QuizContentManagement.tsx.
 *
 * Shape varies by (action, entity_type) — normalization is the frontend's job
 * (ADR OQ2 / spec §Frontend):
 *   - flat row dict            — question_item, learn_lesson/learn_drill UPDATE,
 *                                video_item, reading_item
 *   - flat row + `criteria[]`  — rehearsal_template
 *   - wrapped DELETE subtree   — {"learn_drill": {...}, "questions": [...], ...}
 *
 * feat-198 CAVEAT — a snapshot is a raw `SELECT *` of the row AS IT EXISTED, so
 * PRE-MIGRATION blobs still carry the OLD keys (`unit_id`, `lesson_id`) and the
 * OLD wrapper names. They stay readable; only REPLAY refuses, with a 409 naming
 * feat-198 and stating that the snapshot is still viewable (ADR 0001 §D-J). Do
 * not "normalize" old keys into new ones here — a shim on a write path is what
 * §D-J rejected, and the old key shape IS the epoch marker.
 */
export type CurriculumSnapshotSide = Record<string, unknown> | null;

/**
 * The full detail for ONE history entry: the same metadata the list rows carry,
 * plus the two snapshot blobs the list endpoints deliberately strip to stay
 * small (`_HISTORY_SELECT` returns only `revertable`).
 *
 * `before` is null for a CREATE (nothing existed); `after` is null for a DELETE
 * (nothing remains). UPDATE and REVERT carry both sides.
 *
 * Values are preserved AS STORED — DECIMAL columns and `sort_order` arrive as
 * STRINGS, not numbers. Render them verbatim; do not coerce or do math on them.
 */
export interface CurriculumHistorySnapshot extends CurriculumHistoryEntry {
  before: CurriculumSnapshotSide;
  after: CurriculumSnapshotSide;
}

/**
 * Fetch the stored before/after snapshot for a single history entry — the read
 * behind the expandable Before | After view in `CurriculumHistoryPanel`.
 *
 * Tenancy resolves server-side from the history row's OWN stored `tenant_id`
 * (never a live-curriculum join), so a hard-deleted item's snapshot stays
 * readable — which is exactly what lets a manager see what a deleted item
 * contained before restoring it. Fails closed: an unknown id and a foreign-tenant
 * id are BOTH 404, never 403, so the response can't confirm existence (AC5).
 * fetchApi throws on both, indistinguishably by design.
 */
export async function getHistorySnapshot(
  historyId: string,
): Promise<CurriculumHistorySnapshot> {
  return fetchApi<CurriculumHistorySnapshot>(
    `/api/learn-content/history/${encodeURIComponent(historyId)}/snapshot`,
    { headers: getAuthHeaders() },
  );
}

/**
 * Single source of truth mapping each editor data structure to its DB
 * `entity_type`. Import this everywhere the history API needs a type string so
 * the UI-label ≠ DB-name trap (see block comment above) can never drift.
 */
export const CURRICULUM_ENTITY_TYPE = {
  // feat-235. All three CAPTURE to history; NONE of them is revertable — the
  // backend lists them in `_NON_REVERTABLE_ENTITY_TYPES` because `_revert_delete`
  // re-inserts children with no parent-existence check, so restoring a membership
  // snapshot could resurrect a row pointing at a deleted lesson.
  track: 'learn_track',
  trackLessons: 'learn_track_lesson',
  trackPrereqs: 'learn_track_prereq',
  unit: 'learn_lesson',
  lesson: 'learn_drill',
  question: 'question_item',
  rehearsal: 'rehearsal_template',
  video: 'video_item', // feat-165
  reading: 'reading_item', // feat-166
} as const satisfies Record<string, CurriculumEntityType>;

// ============================================================
// feat-302 — AUDIENCE SCOPE (ADR 0001 §10). Backend: the audience handlers in
// routes/admin_quiz_content.py.
//
// A Track and a Lesson each carry an audience: per dimension (locations, roles)
// a mode and a list. The client never evaluates the rule — chips summarise the
// stored object, and View as filters the loaded tree to the id sets the backend
// returns from `audience-preview`. No TypeScript twin of the scope rule.
// ============================================================

export type AudienceMode = 'everyone' | 'only' | 'except';

/** ADR §10.1 — read and write share one shape. `everyone` always pairs with an
 *  empty list. Lists come back sorted (locations by name, roles by display name). */
export interface Audience {
  locations: { mode: AudienceMode; ids: string[] };
  roles: { mode: AudienceMode; keys: string[] };
}

export interface AudienceLocationOption {
  id: string;
  name: string;
}

export interface AudienceRoleOption {
  role_key: string;
  display_name: string;
}

/** ADR §10.2 — top-level on `GET /learn-tracks`. */
export interface AudienceOptions {
  locations: AudienceLocationOption[];
  roles: AudienceRoleOption[];
}

/** The unscoped audience. A fresh object every call — never shared/mutated. */
export function everyoneAudience(): Audience {
  return { locations: { mode: 'everyone', ids: [] }, roles: { mode: 'everyone', keys: [] } };
}

/** The entity's audience, or everyone/everyone when the payload predates feat-302. */
export function audienceOf(entity: { audience?: Audience | null }): Audience {
  return entity.audience ?? everyoneAudience();
}

/** ADR §10.3 — replace a Track's whole audience. Returns the stored, canonical
 *  object. 422 on an empty `only`/`except` list or a foreign id. */
export async function putTrackAudience(trackId: string, audience: Audience): Promise<Audience> {
  return fetchApi<Audience>(`/api/learn-tracks/${encodeURIComponent(trackId)}/audience`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: audience,
  });
}

/** ADR §10.3 — replace a Lesson's (`learn_lesson` = `QuizUnit`) whole audience. */
export async function putLessonAudience(lessonId: string, audience: Audience): Promise<Audience> {
  return fetchApi<Audience>(`/api/learn-lessons/${encodeURIComponent(lessonId)}/audience`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: audience,
  });
}

/** ADR §10.4 response. */
export interface AudiencePreview {
  track_ids: string[];
  lesson_ids: string[];
}

/**
 * ADR §10.4 — the Track and Lesson ids a learner at `locationId` holding the
 * single role `roleKey` (not multi-location) would see, BY SCOPE ONLY. Either
 * may be null (that dimension unfiltered). Callers skip the request when both
 * are null.
 */
export async function getAudiencePreview(
  tenantId: string,
  locationId: string | null,
  roleKey: string | null,
): Promise<AudiencePreview> {
  const params = new URLSearchParams();
  if (locationId) params.set('location_id', locationId);
  if (roleKey) params.set('role_key', roleKey);
  const qs = params.toString();
  return fetchApi<AudiencePreview>(
    `/api/tenant/${encodeURIComponent(tenantId)}/learn-content/audience-preview${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() },
  );
}
